import { AfterViewInit, ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DocumentManagerService,
    SyncService,
    getDocumentAppointmentAttachments,
    getDocumentAttachments,
    getDocumentComplaintSpareParts,
    getDocumentNoteAttachments,
    getDocumentPositions,
    getDocumentsAttachments,
    getSelected,
    getSelectedComplaint,
    getSelectedComplaintAppointments,
    getSelectedComplaintPositions,
    getSelectedComplaintSpareParts,
    getSelectedId,
    getSynced,
    openDoc,
} from '@icc/b2b/sales-document';
import {
    AssignPermission,
    ClockService,
    Complaint,
    ComplaintAppointment,
    ComplaintData,
    ComplaintHandlingLevel,
    ComplaintNote,
    ComplaintPosition,
    ComplaintsDocumentType,
    ComplaintsDocumentTypes,
    ComplaintSparePart,
    DocumentAttachment,
    DocumentChange,
    editableAppointmentStatuses,
    openedAppointmentStatuses,
    PersonsService,
    ProductsList,
    State,
    Synced,
    Workflow,
} from '@icc/b2b/shared';
import { core, parseDate } from '@icc/shared/helpers';
import { Store } from '@ngrx/store';
import {
    Observable,
    ReplaySubject,
    Subscription,
    combineLatest,
    lastValueFrom,
    startWith,
} from 'rxjs';
import { ComplaintService } from '../complaint.service';
import { ConfigService, TranslateService } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';
import { delay, filter, map, switchMap, take } from 'rxjs/operators';
import { DialogService, InputComponent } from '@icc/ui';
import { HttpClient } from '@angular/common/http';
import { ComplaintDocumentPreviewComponent } from '../complaint-document-preview/complaint-document-preview.component';
import { StatsService } from '@icc/shared/helpers/b2b';
import { ProductsService } from 'libs/b2b/trade/src/lib/products.service'
import { ComplaintAppointmentPreviewComponent } from '../complaint-appointment-preview/complaint-appointment-preview.component';
import { ComplaintUserPreviewDialogComponent } from '../complaint-user-preview-dialog/complaint-user-preview-dialog.component';
import { ComplaintParty } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintChangeUserDialogComponent } from '../complaint-change-user-dialog/complaint-change-user-dialog.component';
import { ComplaintMapDialogComponent } from '../complaint-map-dialog/complaint-map-dialog.component';
import { ComplaintInfoComponent } from '../complaint-info/complaint-info.component';
import { ComplaintButton } from 'libs/b2b/shared/src/lib/models/complaint-workflow-rule';
import { GoogleMapsLoaderService } from '../google-maps-loader.service';
import {
    ComplaintComplainantDataChanges,
    ComplaintComplainantDataDialogComponent,
} from '../complaint-complainant-data-dialog/complaint-complainant-data-dialog.component';

@Component({
    selector: 'icc-complaint',
    templateUrl: './complaint.component.html',
    styleUrls: ['./complaint.component.scss'],
})
export class ComplaintComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    documentUuid = '';

    positions$ = this.store.select(getSelectedComplaintPositions).pipe(
        map(
            (pos) =>
                pos.map((p) => ({
                    ...p,
                    configuration: p.configuration ? core.parseJson(p.configuration) : null,
                })) as ComplaintPosition[]
        )
    );

    positionsShowDetails = new Map<string, boolean>();
    positionsTitles = new Map<string, string[]>();
    positionsIndexes = new Map<string, string>();
    positionsWorkflows = new Map<string, Workflow | null>();
    positionsWorkflowsGroups = new Map<number, ComplaintPosition[]>();
    positionsToVerify: ComplaintPosition[] = [];
    multipleWorkflows = false;
    selectedPositions: string[] = [];

    complaintPositions: ComplaintPosition[] = [];

    complaintSpareParts$ = this.store.select(getSelectedComplaintSpareParts);
    complaintSpareParts: ComplaintSparePart[] = [];

    usersMap: Map<number, string> = new Map();

    appointments$ = this.store.select(getSelectedComplaintAppointments).pipe(
        map((appointments) =>
            appointments
                .slice()
                .sort((a, b) => {
                    const aDateOnly = typeof a.date === 'object'
                        ? a.date.toISOString().split('T')[0]
                        : a.date;
                    let aTime = a.time;
                    if (!aTime) {
                        aTime = '00:00';
                    }
                    if (aTime && !aTime.includes(':')) {
                        aTime += ':00';
                    }
                    const aDateTime = new Date(
                        aDateOnly + (aTime ? 'T' + aTime : '')
                    ).getTime();

                    const bDateOnly = typeof b.date === 'object'
                        ? b.date.toISOString().split('T')[0]
                        : b.date;
                    let bTime = b.time;
                    if (!bTime) {
                        bTime = '00:00';
                    }
                    if (bTime && !bTime.includes(':')) {
                        bTime += ':00';
                    }
                    const bDateTime = new Date(
                        bDateOnly + (bTime ? 'T' + bTime : '')
                    ).getTime();

                    return bDateTime - aDateTime;
                })
                .map((a) => ({
                    ...a,
                })) as ComplaintAppointment[]
        )
    );

    complaintsAppointmentsMap: Map<string, ComplaintAppointment> = new Map<string, ComplaintAppointment>();
    complaintAppointments: ComplaintAppointment[] = [];

    productId = 0;
    productsList!: ProductsList;

    activeUserCompanyId: number;

    openedDocumentId$ = this.store.select(getSelectedId);
    openedDocument$ = this.store.select(getSelectedComplaint) as Observable<Complaint>;
    complaintsDocument$ = this.openedDocument$.pipe(
        map((document) =>
            document &&
            (Number(document.authorizedRetailerCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany) ||
                Number(document.distributorCompanyId) ===
                    Number(this.configService?.user?.user?.ActiveCompany) ||
                document.formerDistributors?.[
                    Number(this.configService?.user?.user?.ActiveCompany)
                ] ||
                Number(document.manufacturerCompanyId) ===
                    Number(this.configService?.user?.user?.ActiveCompany))
                ? document
                : null
        )
    );

    openedDocuments: Record<string, Complaint> = {};

    complaintsPositionsMap: Map<string, ComplaintPosition> = new Map<string, ComplaintPosition>();

    attachments$ = this.openedDocument$.pipe(
        switchMap((document) => this.store.select(getDocumentsAttachments([document.uuid])))
    );

    appointmentAttachments$ = this.openedDocument$.pipe(
        switchMap((document) => this.store.select(getDocumentAppointmentAttachments([document.uuid])))
    );

    complaintNoteAttachments$ = this.openedDocument$.pipe(
        switchMap((document) => this.store.select(getDocumentNoteAttachments([document.uuid])))
    );

    allChanges: DocumentChange[] = [];

    changes: (
        | DocumentChange & {
              group?: DocumentChange[];
              positions?: ComplaintPosition[];
              appointments?: ComplaintAppointment[];
              complaintNotes?: ComplaintNote[];
              complaintSpareParts?: ComplaintSparePart[];
              attachments?: DocumentAttachment[];
              id: number;
          }
    )[] = [];

    changesLoading = true;

    private loadedChangesDocumentUuid = '';
    private loadingChangesDocumentUuid = '';
    private loadingChangesPromise: Promise<void> | null = null;

    activeHistoryTab: 'notes' | 'system' | 'all' = 'notes';

    get filteredChanges() {
        if (this.activeHistoryTab === 'notes') {
            return this.changes.filter((change) => change.type === 'addComplaintNote' || change.type === 'splitSeparator');
        }
        if (this.activeHistoryTab === 'system') {
            return this.changes.filter((change) => change.type !== 'addComplaintNote');
        }
        if (this.activeHistoryTab === 'all') {
            return this.changes;
        }
        return this.changes;
    }

    syncedDocumentsState = new Map<string, Synced>();
    synced: Synced = Synced.Offline;
    complaintType: 'complaint' | 'service' = 'complaint';
    hasAccessTo = {
        manageComplaint: false,
        manageComplaintManufacturer: false,
        addComplaintNote: false,
        addInternalNote: false,
        addPrivateNote: false,
        editAssign: false,
        scheduleAppointment: false,
        manageProtocolOnly: false,
        manageSpareParts: false,
        appealToDistributor: false,
        appealToManufacturer: false,
        receivedFromManufacturer: false,
        receivedFromDistributor: false,
        partsSentAps: false,
        returnResignationAps: false,
        partsSentDistributor: false,
        returnResignationDistributor: false,
        editComplainantData: false,
    };

    private subscriptions: Subscription[] = [];

    origin: 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;

    navButtonLabel = this.translateService.instant('COMPLAINT|Powrót do listy');

    positionsGroups: Map<string, ComplaintPosition[]> = new Map();

    showFullPositionTitles = false;

    apiLoaded = false;

    documentTypes = ComplaintsDocumentTypes;
    preview = false;

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;
    isDistributorAsAps = false;
    canAttachAps = false;
    canDetachAps = false;
    isComplaintAuthorCompany = false;

    private scrollPending = false;

    constructor(
        private store: Store<State>,
        private route: ActivatedRoute,
        private complaintService: ComplaintService,
        private documentManagerService: DocumentManagerService,
        private configService: ConfigService,
        private router: Router,
        private translateService: TranslateService,
        private syncService: SyncService,
        private permissionsService: NgxPermissionsService,
        private http: HttpClient,
        private dialogService: DialogService,
        private chRef: ChangeDetectorRef,
        private statsService: StatsService,
        private productsService: ProductsService,
        private googleMapsLoaderService: GoogleMapsLoaderService
    ) {
        this.origin = this.route.snapshot.queryParamMap.get('origin') as 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;
        this.activeUserCompanyId = Number(this.configService?.user?.user?.ActiveCompany);
        this.showFullPositionTitles = this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
        this.setNavButtonLabel();
    }

    async ngOnInit() {
        await this.complaintService.workflowsLoaded;
        this.loadGoogleMapsApi();
        this.store.dispatch(openDoc({ id: this.route.snapshot.params.id }));
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                this.store.dispatch(openDoc({ id: params.id }));
            }),
            combineLatest([this.complaintsDocument$.pipe(startWith(null))]).subscribe(
                ([complaintsDocument]) => {
                    const documents: Record<string, Complaint> = {};
                    const openedDocuments = [];
                    const nextDocumentUuid = complaintsDocument?.uuid ?? '';
                    const documentChanged = this.documentUuid !== nextDocumentUuid;
                    this.documentUuid = nextDocumentUuid;
                    if (documentChanged) {
                        this.resetChanges();
                    }
                    this.complaintType =
                        Number(complaintsDocument?.documentTypeId) === ComplaintsDocumentType.Service ? 'service' : 'complaint';
                    this.setDocumentCrumbs();
                    this.loadChanges();

                    if (complaintsDocument) {
                        documents[complaintsDocument.uuid] = complaintsDocument;
                        openedDocuments.push(complaintsDocument.uuid);
                    }

                    this.statsService.add({
                        action: 'open-complaint',
                    });

                    this.openedDocuments = documents;
                    this.syncService.setCurrentDocumentsIds(Array.from(new Set(openedDocuments)));
                }
            ),
            combineLatest([this.positions$]).subscribe(async ([positions]) => {
                const complaintsPositionsMap: Map<string, ComplaintPosition> = new Map();
                const positionsShowDetails = new Map<string, boolean>();
                const positionsTitles = new Map<string, string[]>();
                this.positionsToVerify = [];
                positions.forEach((position) => {
                    complaintsPositionsMap.set(position.uuid, position);
                    positionsShowDetails.set(
                        position.uuid,
                        this.positionsShowDetails.get(position.uuid) ?? true
                    );
                    positionsTitles.set(position.uuid, this.complaintService.getPositionTitle(position, this.showFullPositionTitles));
                    if (position.toVerify) {
                        this.positionsToVerify.push(position);
                    }
                });
                this.complaintsPositionsMap = complaintsPositionsMap;
                this.positionsShowDetails = positionsShowDetails;
                this.positionsTitles = positionsTitles;
                this.complaintPositions = positions;

            }),
            combineLatest([this.appointments$]).subscribe(async ([appointments]) => {
                await this.complaintService.loaded;
                const complaintsAppointmentsMap: Map<string, ComplaintAppointment> = new Map();
                appointments.forEach((appointment) => {
                    complaintsAppointmentsMap.set(appointment.uuid, appointment);
                });
                this.complaintsAppointmentsMap = complaintsAppointmentsMap;
                this.complaintAppointments = appointments;
                this.evaluateAppointmentAvailability();
            }),
            combineLatest([
                this.openedDocument$,
                this.openedDocumentId$,
                this.permissionsService.permissions$,
            ]).subscribe(([document, documentId]) => {
                if (!document && documentId) {
                    this.store.dispatch(openDoc({ id: null }));
                    this.router.navigate(['/trade']);
                }
            }),
            combineLatest([
                this.openedDocument$,
                this.positions$,
            ]).subscribe(([document, positions]) => {
                if (document) {
                    this.checkPositionsWorkflow(document, positions);
                    this.checkAccess();
                    this.isDistributorAsAps = !!this.openedDocuments[this.documentUuid].distributorCompanyId &&
                        !JSON.parse(this.openedDocuments[this.documentUuid].authorizedRetailerCompanyId + '');
                    this.evaluateApsButtonsAvailability();
                }
            }),
            this.complaintSpareParts$.subscribe((complaintSpareParts) => {
                this.complaintSpareParts = complaintSpareParts;
            }),
            this.store.select(getSynced).subscribe(({ synced }) => {
                this.syncedDocumentsState = synced;
                this.updatedSynced();
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
        if (this.openedDocuments[this.documentUuid]) {
            this.authorizedRetailerDistributorComplaint = this.openedDocuments[this.documentUuid].clientName?.length < 1 && this.openedDocuments[this.documentUuid].authorizedRetailerCompanyId !== null;
            this.distributorManufacturerComplaint = this.openedDocuments[this.documentUuid].clientName?.length < 1 && this.openedDocuments[this.documentUuid].authorizedRetailerCompanyId === null;
            // Check if distributor is acting as APS (dystrybutor = APS)
            this.isDistributorAsAps = !!this.openedDocuments[this.documentUuid].distributorCompanyId &&
                !JSON.parse(this.openedDocuments[this.documentUuid].authorizedRetailerCompanyId + '');
        }
        await this.loadProductsList();
    }

    ngAfterViewInit(): void {
        let top = document.getElementById('top');
        const appointmentUuid = this.route.snapshot.queryParamMap.get('appointmentUuid');
        if (appointmentUuid !== null) {
            if (!this.scrollPending) {
                this.scrollPending = true;

                this.appointments$.pipe(
                    filter(appointments => appointments && appointments.length > 0),
                    take(1),
                    delay(10)
                ).subscribe(() => {
                    const appointmentToScrollTo = document.getElementById(appointmentUuid);
                    if (appointmentToScrollTo !== null) {
                        this.scrollToSection('appointments');
                    }
                    this.scrollPending = false;
                });
            }
        } else if (top !== null) {
            top.scrollIntoView();
            top = null;
        }
    }

    ngOnChanges() {
        this.checkAccess();
    }

    ngOnDestroy() {
        this.subscriptions.map((el) => el.unsubscribe());
    }

    async checkAccess() {
        const currentDocument = this.openedDocuments[this.documentUuid];
        const hasManagePermission = await this.permissionsService.hasPermission('complaints-manage');
        const hasManageProtocolPermission = await this.permissionsService.hasPermission('complaints-manage-protocol');

        // Check if user's company is the complaint creator in internal processes
        this.isComplaintAuthorCompany = false;
        if (this.positionsWorkflowsGroups?.size === 1) {
            const workflowId = this.positionsWorkflowsGroups.keys().next().value;
            if (workflowId !== undefined) {
                const workflow = this.complaintService.workflows.find(w => +w.id === +workflowId);
                const userCompanyId = this.configService.user?.company?.id;

                if (workflow?.complaintCreator === 'authorized_retailer' && userCompanyId && currentDocument.authorizedRetailerCompanyId) {
                    this.isComplaintAuthorCompany = +currentDocument.authorizedRetailerCompanyId === +userCompanyId;
                } else if (workflow?.complaintCreator === 'distributor' && userCompanyId && currentDocument.distributorCompanyId) {
                    this.isComplaintAuthorCompany = +currentDocument.distributorCompanyId === +userCompanyId;
                }
            }
        }

        this.hasAccessTo.manageComplaint = hasManagePermission &&
            this.currentStatusAllowEdit() &&
            ((currentDocument.assignedTo && currentDocument.assignedTo.startsWith(this.getUserType())) ||
            await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.editComplainantData =
            !!currentDocument &&
            hasManagePermission &&
            this.hasEndCustomerComplainant(currentDocument);
        this.hasAccessTo.manageComplaintManufacturer = await this.permissionsService.hasPermission('complaints-manage-manufacturer');
        this.hasAccessTo.addComplaintNote = (hasManagePermission || this.isComplaintAuthorCompany) &&
            this.currentStatusAllowEdit();
        this.hasAccessTo.addInternalNote = await this.permissionsService.hasPermission('complaints-add-internal-note') &&
            this.currentStatusAllowEdit();
        this.hasAccessTo.addPrivateNote = await this.permissionsService.hasPermission('complaints-add-private-note') &&
            this.currentStatusAllowEdit();
        this.hasAccessTo.manageProtocolOnly = hasManageProtocolPermission && !hasManagePermission;
        const hasSparePartsPermission = await this.permissionsService.hasPermission('complaints-manage-spare-parts');
        this.hasAccessTo.manageSpareParts = !!hasSparePartsPermission &&
            this.hasAccessTo.manageComplaint &&
            currentDocument.assignedTo !== 'manufacturer' &&
            currentDocument.distributorCompanyId === this.activeUserCompanyId &&
            !(currentDocument.statusId && this.getStatus(currentDocument.statusId)?.closed) &&
            !!currentDocument.assignedTo &&
            currentDocument.assignedTo.startsWith(this.getUserType());
        this.updateButtonAvailability();
    }

    async evaluateAppointmentAvailability() {
        if (this.positionsWorkflowsGroups?.size === 1) {
            const document = this.openedDocuments[this.documentUuid];
            const documentStatusId = this.complaintService.getDocumentStatusForUser(document, this.getUserType());
            const positionsWorkflow = this.complaintService.workflows.find(
                (el) => +el.id === +this.positionsWorkflowsGroups.keys().next().value
            );
            const complaintHandlingLevel: ComplaintHandlingLevel = this.complaintService.getComplaintHandlingLevel(document);
            const rolesStatuses = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                el.level === complaintHandlingLevel &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            if (!rolesStatuses.length) {
                this.hasAccessTo.scheduleAppointment = false;
                return;
            }
            const isAppointmentInProgress = this.complaintAppointments.some(
                (el) =>openedAppointmentStatuses.some(status => status.value === el.status) &&
                    +el.assignedCompanyId! === +(this.configService.user?.user?.ActiveCompany || 0)
            );
            this.hasAccessTo.scheduleAppointment = rolesStatuses.some(
                el => el.buttons[documentStatusId]?.create_appointment?.available
            ) && !isAppointmentInProgress && this.currentStatusAllowEdit();
        }
    }

    async evaluateApsButtonsAvailability() {
        const document = this.openedDocuments[this.documentUuid];
        if (!document) {
            this.canAttachAps = false;
            this.canDetachAps = false;
            return;
        }

        this.canAttachAps = await this.complaintService.isButtonAvailable(
            document,
            this.positionsWorkflowsGroups,
            'attach_aps'
        );

        this.canDetachAps = await this.complaintService.isButtonAvailable(
            document,
            this.positionsWorkflowsGroups,
            'detach_aps'
        );
    }

    canChangeAps() {
        const document = this.openedDocuments[this.documentUuid];
        return document.authorizedRetailerCompanyId &&this.canChangeUser('authorizedRetailer') || this.canAttachAps;
    }

    scrollToTop() {
        const top = document.getElementById('top');
        if (top !== null) {
            top.scrollIntoView({ behavior: 'smooth' });
        }
    }

    scrollToSection(section: string) {
        const element = document.getElementById(section);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    updatedSynced() {
        const previousSynced = this.synced;
        this.synced = this.syncService.getSynced([this.documentUuid], this.syncedDocumentsState);
        if (this.synced === Synced.Synced && previousSynced !== Synced.Synced) {
            this.loadChanges(true);
        }
    }

    setDocumentCrumbs() {
        const crumbs = [];
        let label = '';
        const commands = [
            'trade',
            'complaint',
            'list',
            (this.origin === 'appointment-list' || this.origin === 'appointment-map') ? 'appointments' : 'complaints',
        ];
        label =
        (this.origin === 'appointment-list' ? this.translateService.instant('COMPLAINT_APPOINTMENT|Lista wizyt') :
        (this.origin === 'appointment-map' ? this.translateService.instant('COMPLAINT_APPOINTMENT|Mapa wizyt') :
        (!this.origin || this.origin === 'list' ? this.translateService.instant('COMPLAINT|Lista reklamacji') : this.translateService.instant('COMPLAINT|Mapa reklamacji'))
        ));
        crumbs.push({
            label,
            url: '/' + commands.join('/'),
            origin: this.origin,
        });
        ComplaintComponent.crumbsSubject.next({
            level: 1,
            crumbs,
        });
        if (this.documentUuid) {
            label = this.translateService.instant('COMPLAINT|Obsługa zgłoszenia reklamacyjnego');
            ComplaintComponent.crumbsSubject.next({
                level: 2,
                crumbs: [
                    {
                        label,
                        url: '/trade/complaint/' + this.documentUuid,
                    },
                ],
            });
        }
    }

    switchShowDetails(posId: ComplaintPosition['uuid'], showDetails: boolean) {
        this.positionsShowDetails.set(posId, showDetails);
    }

    trackByPosition(index: number, el: ComplaintPosition) {
        return el.uuid;
    }

    trackByAppointment(index: number, el: ComplaintAppointment) {
        return el.uuid;
    }

    resetChanges() {
        this.allChanges = [];
        this.changes = [];
        this.changesLoading = !!this.documentUuid;
        this.loadedChangesDocumentUuid = '';
    }

    async loadChanges(force = false) {
        const documentUuid = this.documentUuid;
        if (!documentUuid) {
            return;
        }
        if (this.loadingChangesDocumentUuid === documentUuid && this.loadingChangesPromise) {
            return this.loadingChangesPromise;
        }
        if (!force && this.loadedChangesDocumentUuid === documentUuid) {
            return;
        }

        this.loadingChangesDocumentUuid = documentUuid;
        this.changesLoading = this.changes.length === 0;
        this.loadingChangesPromise = this.fetchChanges(documentUuid);
        return this.loadingChangesPromise;
    }

    private async fetchChanges(documentUuid: string) {
        try {
            const result = await lastValueFrom(
                this.http.get<{
                    changes: DocumentChange[];
                }>('/api/b2b/document-history/' + documentUuid + '?groupingSchemaId=0')
            );
            if (result.changes && this.documentUuid === documentUuid) {
                this.changesLoading = false;
                this.allChanges = result.changes;
                this.loadedChangesDocumentUuid = documentUuid;
                await this.groupChanges();
                this.changes.reverse();
                this.chRef.detectChanges();
            }
        } finally {
            if (this.loadingChangesDocumentUuid === documentUuid) {
                this.loadingChangesDocumentUuid = '';
                this.loadingChangesPromise = null;
            }
        }
    }

    async groupChanges() {
        this.changes = [];
        let addDocChange;
        let i = 0;
        let splitSeparatorInserted = false;
        let hasInheritedChanges = false;
        const splitOriginMessageKeys = new Set<string>();
        for (const change of this.allChanges) {
            if (change.isInheritedFromParent) {
                hasInheritedChanges = true;
            }
            // For child documents: insert separator at the transition from inherited to own changes
            if (!splitSeparatorInserted && hasInheritedChanges && !change.isInheritedFromParent) {
                splitSeparatorInserted = true;
                this.changes.push({
                    type: 'splitSeparator',
                    id: i++,
                    title: this.translateService.instant('COMPLAINT|Podział zgłoszenia'),
                } as any);
            }
            if (change.type === 'addDoc') {
                addDocChange = change;
                this.changes.push({
                    ...change,
                    id: i++,
                    positions: [],
                    attachments: [],
                });
            } else if (change.type === 'addPosition') {
                addDocChange = this.changes.find(
                    (el) => el.type === 'addDoc' && el.documentId === change.documentId
                );
                if (addDocChange?.positions) {
                    addDocChange.positions.push(change.value);
                }
                if (change.value?.copiedFromDocumentNumber) {
                    const splitOriginMessageKey = [
                        change.documentId,
                        change.value?.copiedFromDocumentId || '',
                        change.value?.copiedFromDocumentNumber || '',
                    ].join('|');

                    if (splitOriginMessageKeys.has(splitOriginMessageKey)) {
                        continue;
                    }

                    splitOriginMessageKeys.add(splitOriginMessageKey);
                    const copiedFromDescription = change.value.copiedFromDocumentId
                        ? '<a href="/b2b/trade/complaint/' + change.value.copiedFromDocumentId + '">' + change.value.copiedFromDocumentNumber + '</a>'
                        : change.value.copiedFromDocumentNumber;
                    this.changes.push({
                        ...change,
                        id: i++,
                        title: this.translateService.instant('COMPLAINT|Zgłoszenie zostało utworzone po podziale dokumentu'),
                        description: copiedFromDescription,
                        group: [],
                        html: true,
                    });
                }
            } else if (
                change.type === 'addAttachment' &&
                ((parseDate(change.date).getTime() - parseDate(addDocChange?.date).getTime()) /
                    1000 <
                    5)
            ) {
                addDocChange = this.changes.find(
                    (el) => el.type === 'addDoc' && el.documentId === change.documentId
                );
                if (addDocChange?.attachments) {
                    addDocChange.attachments.push(change.value);
                }
            } else if (
                change.type === 'removeAttachment' &&
                this.changes.some(
                    (el) =>
                        el.type === 'setPositionField' &&
                        el.field === 'isDeleted' &&
                        change.positionId === el.positionId &&
                        (parseDate(change.date).getTime() - parseDate(el?.date).getTime()) / 1000 <
                            1
                )
            ) {
                continue;
            } else if (change.type === 'setPositionField' && change.field === 'isDeleted') {
                const movedToDescription = change.value
                    ? '<a href="/b2b/trade/complaint/' + change.value + '">' + change.description + '</a>'
                    : change.description;
                this.changes.push({
                    ...change,
                    id: i++,
                    description: movedToDescription,
                    group: [],
                    html: !!change.value,
                });
            } else if (change.type === 'setPositionField' && change.field === 'configuration') {
                const defectPlace: string[] = [];
                this.showFullPositionTitles ? this.complaintService.getFullDefectPlace(defectPlace, change.description) : this.complaintService.getDefectPlace(defectPlace, change.description);
                this.changes.push({
                    ...change,
                    id: i++,
                    description: defectPlace.join(' > '),
                    group: [],
                });
            } else if (change.type === 'addComplaintNote') {
                this.changes.push({
                    ...change,
                    id: i++,
                    group: [],
                });
            } else if (change.type === 'addAppointment') {
                this.changes.push({
                    ...change,
                    id: i++,
                    group: [],
                });
            } else if (change.type === 'splitedDocument') {
                // For original documents: insert separator at the split point
                this.changes.push({
                    type: 'splitSeparator',
                    id: i++,
                    title: change.title,
                } as any);
            } else if (change.type === 'setField' && change.field === 'complaintOrderNumber' && !(
                await this.permissionsService.hasPermission('complaints-manage') &&
                this.configService.user?.user?.ActiveCompanyLicenseType === 'license'
            )) {
                continue;
            } else if (change.type === 'changedStatus' && change.field === 'manufacturerStatusId' && this.getUserType() === 'authorizedRetailer') {
                continue;
            } else if (change.type === 'setField' && (change.field === 'discount' || change.field === 'correctionNumber') && this.getUserType() !== 'manufacturer' && this.getUserType() !== 'distributor') {
                continue;
            } else if (change.type === 'setPositionField' && (change.field === 'distributorOrderNumber' || change.field === 'distributorOrderPositionNumber') && this.getUserType() !== 'manufacturer') {
                continue;
            } else {
                if (
                    this.changes[this.changes.length - 1]?.group &&
                    change.type === this.changes[this.changes.length - 1]?.type &&
                    (!change?.positionId ||
                        (change?.positionId &&
                            this.changes[this.changes.length - 1]?.positionId ===
                                change?.positionId)) &&
                    this.changes[this.changes.length - 1]?.userId === change?.userId &&
                    !this.changes[this.changes.length - 1]?.group?.some(
                        (el) => el.field && change.field && el.field === change.field
                    ) &&
                    (parseDate(change.date).getTime() -
                        parseDate(this.changes[this.changes.length - 1]?.date).getTime()) /
                        1000 <
                        120
                ) {
                    this.changes[this.changes.length - 1]?.group?.unshift(change);
                } else {
                    this.changes.push({
                        ...change,
                        id: i++,
                        group: [change],
                    });
                }
            }
        }
    }

    showDocumentFirstVersion(
        change: DocumentChange & {
            positions: ComplaintPosition[];
            attachments: DocumentAttachment[];
        }
    ) {
        this.dialogService.open(ComplaintDocumentPreviewComponent, {
            data: {
                document: core.copy(change.value),
                positions: change.positions,
                attachments: change.attachments,
            },
        });
    }

    showAppointmentFirstVersion(
        change: DocumentChange
    ) {
        const document = this.openedDocuments[change.value.documentId]
            || this.openedDocuments[this.documentUuid];
        this.dialogService.open(ComplaintAppointmentPreviewComponent, {
            data: {
                appointment: core.copy(change.value),
                document: core.copy(document),
            }
        });
    }

    trackByFunction(index: number | string, item): number | string {
        return item.id;
    }

    changeComplaintType(documentTypeId: ComplaintsDocumentType) {
        this.complaintType = Number(documentTypeId) === ComplaintsDocumentType.Service ? 'service' : 'complaint';
        this.setDocumentCrumbs();
    }

    async checkPositionsWorkflow(document: Complaint, positions: ComplaintPosition[]) {
        const {positionsWorkflows, positionsWorkflowsGroups} = await this.complaintService.getPositionsWorkflows(document, positions);
        const positionsGroups = this.complaintService.groupPositions(positionsWorkflowsGroups);
        this.positionsWorkflows = positionsWorkflows;
        this.positionsWorkflowsGroups = positionsWorkflowsGroups;
        this.positionsGroups = positionsGroups;
        this.multipleWorkflows = positionsGroups.size > 1;
        if (this.openedDocuments[this.documentUuid] && this.openedDocuments[this.documentUuid].multipleWorkflows !== this.multipleWorkflows) {
            this.complaintService.changeMultipleWorkflows(this.openedDocuments[this.documentUuid], this.multipleWorkflows);
        }
        if (this.openedDocuments[this.documentUuid]) {
            this.evaluateAppointmentAvailability();
            this.evaluateApsButtonsAvailability();
            this.updateButtonAvailability();
        }
    }

    selectPosition(positionId: string) {
        this.selectedPositions.push(positionId);
    }

    unselectPosition(positionId: string) {
        this.selectedPositions = this.selectedPositions.filter((el) => el !== positionId);
    }

    clearSelectedPositions() {
        this.selectedPositions = [];
    }

    editAppointment(complaint: Complaint) {
        const complaintAppointment = {
            uuid: core.generateUUID(),
            documentId: complaint.uuid,
            machineId: ClockService.getClock().timestamp.node(),
            date: null,
            time: null,
            scheduledTime: null,
            timeUsed: null,
            status: 'proposed',
            comment: '',
            assignedUserId: null,
            protocolNotes: '',
            protocolDate: null,
            protocolChangedPartsOrActions: '',
            paidService: false,
            repairCost: null,
            workStatus: 'inProgress',
            workToDo: '',
            protocolUserNotes: '',
            serviceRating: 0,
            orderCompliance: false,
            wingDamages: false,
            roof: false,
            properDoorLocking: false,
            doorAdjustment: false,
            doorMeasurement: false,
        } as ComplaintAppointment;
        this.complaintService.editAppointment(complaint, complaintAppointment, [], false, this.positionsWorkflowsGroups);
    }

    editComplaint(complaint: Complaint) {
        this.complaintService.editComplaint(complaint, this.complaintAppointments, this.positionsWorkflowsGroups, this.positionsWorkflows, this.complaintPositions);
    }

    manageComplaintSpareParts(complaint: Complaint) {
        this.complaintService.manageComplaintSpareParts(complaint);
    }

    createComplaintNote(complaint: Complaint) {
        const complaintNote = {
            uuid: core.generateUUID(),
            documentId: complaint.uuid,
            positionId: null,
            machineId: ClockService.getClock().timestamp.node(),
            content: '',
            visibility: this.hasAccessTo.addInternalNote && !this.hasAccessTo.addComplaintNote ? 'internal' : 'private',
            authorCompanyId: Number(this.configService?.user?.user?.ActiveCompany),
            authorUserId: Number(this.configService?.user?.user?.id),
            replyId: '',
        } as ComplaintNote;
        this.complaintService.createComplaintNote(complaint, complaintNote, this.complaintsPositionsMap, []);
    }

    replyToComplaintNote(
        complaint: Complaint,
        complaintNote: ComplaintNote,
    ) {
        const complaintNoteReply = {
            uuid: core.generateUUID(),
            documentId: complaint.uuid,
            positionId: complaintNote.positionId,
            machineId: ClockService.getClock().timestamp.node(),
            content: '',
            visibility: this.hasAccessTo.addInternalNote && !this.hasAccessTo.addComplaintNote ? 'internal' : complaintNote.visibility,
            authorCompanyId: Number(this.configService?.user?.user?.ActiveCompany),
            authorUserId: Number(this.configService?.user?.user?.id),
            replyId: complaintNote.uuid,
        } as ComplaintNote;
        this.complaintService.createComplaintNote(complaint, complaintNoteReply, this.complaintsPositionsMap, [], true);
    }

    getProductName(productId: number) {
        return this.productsList.find(product => product.id === productId)?.name;
    }

    private async loadProductsList() {
        await this.productsService.loadData().then(() =>
            this.productsService.getData().then((productData) => {
                this.productsList = productData.complaintProductsList;
            })
        );
    }

    getUnit(productId: number) {
        return this.productsList.find(product => product.id === productId)?.price.priceUnits.find(unit => {
            const part = this.complaintSpareParts.find(sparePart => sparePart.productId === productId);
            return unit.id === part?.unitId && unit.code === part?.unitCode;
        });
    }

    removeSparePartFromComplaint(complaintSparePart: ComplaintSparePart) {
        this.complaintService.deleteComplaintSparePart(complaintSparePart);
    }

    getStatus(statusId: number) {
        return this.complaintService.getStatus(statusId);
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    private async loadGoogleMapsApi() {
        await this.complaintService.loaded;
        const data = this.complaintService.data;
        if (data?.googleMapsApiKey) {
            try {
                await this.googleMapsLoaderService.load(data.googleMapsApiKey, ['places']);
                this.apiLoaded = true;
                this.chRef.detectChanges();
            } catch (error) {
                console.error('Google Maps API load error:', error);
                this.apiLoaded = false;
                this.chRef.detectChanges();
            }
        }
    }

    openUserPreviewDialog(userType: string, document: Complaint) {
        const user = this.getUserByType(userType, document);

        const responsibleUser = {
            name: document[userType as 'authorizedRetailer' | 'distributor' | 'manufacturer']?.referenceName,
            phoneNumber: document[userType as 'authorizedRetailer' | 'distributor' | 'manufacturer']?.phoneNumber,
            email: document[userType as 'authorizedRetailer' | 'distributor' | 'manufacturer']?.email,
        }

        const assignedUser = {
            name: document[userType + 'UserName' as 'authorizedRetailerUserName' | 'distributorUserName' | 'manufacturerUserName'],
            phoneNumber: document[userType + 'PhoneNumber' as 'authorizedRetailerPhoneNumber' | 'distributorPhoneNumber' | 'manufacturerPhoneNumber'],
            email: document[userType + 'Email' as 'authorizedRetailerEmail' | 'distributorEmail' | 'manufacturerEmail'],
        }

        this.dialogService
            .open(ComplaintUserPreviewDialogComponent, {
                data: {
                    user,
                    userType,
                    responsibleUser,
                    assignedUser,
                    locationDescription: document.locationDescription,
                    locationSource: document.locationSource,
                    locationPartialMatchConfirmed: document.locationPartialMatchConfirmed,
                    latitude: document.latitude,
                    longitude: document.longitude,
                    apiLoaded: this.apiLoaded,
                },
            })
            .afterClosed();
    }

    openMapDialog(document: Complaint) {
        const dialogData = {
            initialPosition: document.latitude && document.longitude
                ? {
                    lat: document.latitude,
                    lng: document.longitude,
                }
                : undefined
        };

        this.dialogService.open(ComplaintMapDialogComponent, { data: dialogData });
    }

    openComplainantDataDialog(document: Complaint) {
        if (!this.hasEndCustomerComplainant(document)) {
            return;
        }
        this.dialogService
            .open(ComplaintComplainantDataDialogComponent, {
                data: {
                    complaint: document,
                },
            })
            .afterClosed()
            .subscribe((changes: Partial<ComplaintComplainantDataChanges> | null) => {
                if (changes) {
                    this.complaintService.changeComplainantData(document, changes);
                }
            });
    }

    private hasEndCustomerComplainant(document: Complaint): boolean {
        return !!document.clientName?.trim();
    }

    getUserByType(userType: string, document: Complaint): ComplaintParty | undefined {
        return this.complaintService.getUserByType(userType, document);
    }

    canChangeUser(userType: string) {
        return this.complaintService.canChangeUser(userType);
    }

    openChangeUserDialog(userType: string, document: Complaint) {
        const user = this.getUserByType(userType, document);

        this.dialogService
            .open(ComplaintChangeUserDialogComponent, {
                data: {
                    user,
                    userType,
                    companyId: this.configService.user?.user?.ActiveCompanyLicenseType === 'license'
                        ? userType === 'authorizedRetailer'
                            ? document.distributorCompanyId
                            : this.configService.user?.company?.id
                        : document.distributorCompanyId,
                    chooseContractorContractor:
                        this.configService.user?.user?.ActiveCompanyLicenseType === 'license' &&
                        userType === 'authorizedRetailer',
                    complaint: document,
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    const apsCompanyId = document.authorizedRetailerCompanyId;
                    this.complaintService.changeUser(document, data, userType);
                    if (!apsCompanyId && userType === 'authorizedRetailer') {
                        const newStatus = this.complaintService.statuses.find(status => status.complaintHandlingLevel === 'authorizedRetailer' && status.documentTypeId === document.documentTypeId && status.new)?.id;
                        if (newStatus) {
                            this.complaintService.changeStatus(document, newStatus, 'authorizedRetailer', this.positionsWorkflowsGroups, true);
                            this.complaintService.changeStatus(document, null, 'distributor', this.positionsWorkflowsGroups, true);
                            if (document.manufacturerStatusId) {
                                this.complaintService.changeStatus(document, null, 'manufacturer', this.positionsWorkflowsGroups, true);
                            }
                            this.complaintService.changeAssignedTo(document, 'authorizedRetailer');
                        }
                    }
                }
            });
    }

    editCorrectionAndDiscount(complaint: Complaint) {
        this.complaintService.editCorrectionAndDiscount(complaint);
    }

    detachAps(document: Complaint) {
        const emptyUser = {
            userId: null,
            companyId: null,
            referenceName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            address: '',
            companyVatNr: '',
            companyVatNrEu: '',
        };
        this.complaintService.changeUser(document, emptyUser as any, 'authorizedRetailer');
        const newStatus = this.complaintService.statuses.find(status => status.complaintHandlingLevel === 'clientDistributor' && status.documentTypeId === document.documentTypeId && status.new)?.id;
        if (newStatus) {
            this.complaintService.changeStatus(document, newStatus, 'distributor', this.positionsWorkflowsGroups, true);
            this.complaintService.changeStatus(document, null, 'authorizedRetailer', this.positionsWorkflowsGroups, true);
            if (document.manufacturerStatusId) {
                this.complaintService.changeStatus(document, null, 'manufacturer', this.positionsWorkflowsGroups, true);
            }
            this.complaintService.changeAssignedTo(document, 'distributor');
        }
    }

    private currentStatusAllowEdit(): boolean {
        if (!this.documentUuid || !this.openedDocuments[this.documentUuid]) {
            return false;
        }

        const userType = this.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.openedDocuments[this.documentUuid], userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.getStatus(statusId);
        return status?.allowEdit ?? false;
    }

    getImageAttachments(attachments: DocumentAttachment[] | null | undefined): DocumentAttachment[] {
        return attachments?.filter(att => att && att.fileType.includes('image/')) || [];
    }

    getPdfAttachments(attachments: DocumentAttachment[] | null | undefined): DocumentAttachment[] {
        return attachments?.filter(att => att && att.fileType === 'application/pdf') || [];
    }

    isNotePositionMissing(change: DocumentChange): boolean {
        if (change.type === 'addComplaintNote') {
            if (!change.value?.positionId) {
                return false;
            }
            const positionId = change.value.positionId;
            return !this.complaintPositions.some(p => p.uuid === positionId || p.copiedFromPositionId === positionId);
        }
        if (change.positionId) {
            return !this.complaintPositions.some(p => p.uuid === change.positionId || p.copiedFromPositionId === change.positionId);
        }
        return false;
    }

    canReplyToNote(change: DocumentChange): boolean {
        if (change.type !== 'addComplaintNote') {
            return false;
        }
        if (!change.value?.positionId) {
            return true;
        }
        const positionId = change.value.positionId;
        return this.complaintPositions.some(
            (p) => p.uuid === positionId || p.copiedFromPositionId === positionId
        );
    }

    backToList() {
        let target = 'complaints';
        if (this.origin === 'appointment-list' || this.origin === 'appointment-map') {
            target = 'appointments';
        }
        const commands = ['trade', 'complaint',  'list', target];
        this.router.navigate(commands, { queryParams: { origin: this.origin } });
    }

    acceptChanges() {
        this.complaintService.acceptChanges(this.openedDocuments[this.documentUuid], this.positionsWorkflowsGroups);
    }

    movePositionsToNewDocument() {
        const document = this.openedDocuments[this.documentUuid];
        const positions = this.complaintPositions.filter((p) => this.selectedPositions.includes(p.uuid));
        this.complaintService.movePositionsToNewDocument(document, this.complaintPositions, this.selectedPositions);
        this.clearSelectedPositions();
    }

    showInfo() {
        this.dialogService.open(ComplaintInfoComponent, {
            data: {
                info: this.translateService.instant('COMPLAINT|Podział jest wymagany ze względu na różne procesy rozpatrywania reklamacji lub ze względu na różne numery faktury/zamówienia/seryjny. WAŻNE! Przed dokonaniem podziału upewnij się, że wszystkie numery faktury/zamówienia/seryjny są właściwe i czy miejsca wystąpienia usterki są prawidłowe.'),
            },
        });
    }

    splitNeeded() {
        return this.positionsGroups.size > 1;
    }

    setNavButtonLabel() {
        if (this.origin === 'list' || this.origin === 'appointment-list') {
            this.navButtonLabel = this.translateService.instant('COMPLAINT|Powrót do listy');
        } else if (this.origin === 'map' || this.origin === 'appointment-map') {
            this.navButtonLabel = this.translateService.instant('COMPLAINT|Powrót do mapy');
        }
    }

    workflowButtonClicked(button: ComplaintButton) {
        const document = this.openedDocuments[this.documentUuid];
        this.complaintService.triggerWorkflow('button_click', document, {
            button,
        }, this.positionsWorkflowsGroups, 0, new Set());
    }

    updateButtonAvailability() {
        // UWAGA: Poniższa logika inline jest niezależna od COMPLAINT_BUTTON_CONFIG
        // (complaint-helpers.ts). COMPLAINT_BUTTON_CONFIG jest używana przez
        // ComplaintService.isButtonAvailable() (attach_aps, detach_aps)
        // oraz ComplaintDiagnosticService (diagnostyka).
        if (this.positionsWorkflowsGroups.size === 1) {
            const document = this.openedDocuments[this.documentUuid];
            const distributorDocumentStatusId = this.complaintService.getDocumentStatusForUser(document, 'distributor');
            const manufacturerDocumentStatusId = this.complaintService.getDocumentStatusForUser(document, 'manufacturer');
            const positionsWorkflow = this.complaintService.workflows.find(
                (el) => +el.id === +this.positionsWorkflowsGroups.keys().next().value
            );
            const distributorRolesStatuses = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                (document.authorizedRetailerCompanyId && el.level === 'distributor' || el.level === 'clientDistributor') &&
                this.getUserType() === 'authorizedRetailer' &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            const distributorRolesStatusesForDistributor = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                el.level === 'distributor' &&
                this.getUserType() === 'distributor' &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            const manufacturerRolesStatuses = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                el.level === 'manufacturer' &&
                this.getUserType() === 'distributor' &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            if (!distributorRolesStatuses.length) {
                this.hasAccessTo.appealToDistributor = false;
                this.hasAccessTo.receivedFromDistributor = false;
            } else {
                this.hasAccessTo.appealToDistributor = this.getUserType() === 'authorizedRetailer' &&distributorRolesStatuses.some(
                    el => el.buttons[distributorDocumentStatusId]?.appeal_to_distributor?.available
                );
                this.hasAccessTo.receivedFromDistributor = this.getUserType() === 'authorizedRetailer' &&distributorRolesStatuses.some(
                    el => el.buttons[distributorDocumentStatusId]?.received_from_distributor?.available
                );
            }

            if (!manufacturerRolesStatuses.length) {
                this.hasAccessTo.appealToManufacturer = false;
                this.hasAccessTo.receivedFromManufacturer = false;
            } else {
                this.hasAccessTo.receivedFromManufacturer = this.getUserType() === 'distributor' && manufacturerRolesStatuses.some(
                    el => el.buttons[manufacturerDocumentStatusId]?.received_from_manufacturer?.available
                );
                this.hasAccessTo.appealToManufacturer = this.getUserType() === 'distributor' && manufacturerRolesStatuses.some(
                    el => el.buttons[manufacturerDocumentStatusId]?.appeal_to_manufacturer?.available
                );
            }

            if (distributorRolesStatuses.length) {
                this.hasAccessTo.partsSentAps = distributorRolesStatuses.some(
                    el => el.buttons[distributorDocumentStatusId]?.parts_sent_aps?.available
                );
                this.hasAccessTo.returnResignationAps = distributorRolesStatuses.some(
                    el => el.buttons[distributorDocumentStatusId]?.return_resignation_aps?.available
                );
            } else {
                this.hasAccessTo.partsSentAps = false;
                this.hasAccessTo.returnResignationAps = false;
            }

            const allDistributorRolesStatuses = [...distributorRolesStatusesForDistributor, ...manufacturerRolesStatuses];
            if (allDistributorRolesStatuses.length) {
                this.hasAccessTo.partsSentDistributor = allDistributorRolesStatuses.some(
                    el => el.buttons[
                        el.level === 'distributor' ? distributorDocumentStatusId : manufacturerDocumentStatusId
                    ]?.parts_sent_distributor?.available
                );
                this.hasAccessTo.returnResignationDistributor = allDistributorRolesStatuses.some(
                    el => el.buttons[
                        el.level === 'distributor' ? distributorDocumentStatusId : manufacturerDocumentStatusId
                    ]?.return_resignation_distributor?.available
                );
            } else {
                this.hasAccessTo.partsSentDistributor = false;
                this.hasAccessTo.returnResignationDistributor = false;
            }
        } else {
            this.hasAccessTo.appealToDistributor = false;
            this.hasAccessTo.receivedFromDistributor = false;
            this.hasAccessTo.appealToManufacturer = false;
            this.hasAccessTo.receivedFromManufacturer = false;
            this.hasAccessTo.partsSentAps = false;
            this.hasAccessTo.returnResignationAps = false;
            this.hasAccessTo.partsSentDistributor = false;
            this.hasAccessTo.returnResignationDistributor = false;
        }
    }
}
